/**
 * Read the frequentist variant map out of one goal's stored significance payload.
 *
 * The stored shape changed on this branch. Rows written before it keyed variants
 * directly under the goal:
 *
 *     significance_data[currency][goal][variantId]
 *
 * Rows written now nest by evaluation method and then by correction, because the same
 * comparison has a different verdict under Holm-Bonferroni than under Benjamini-Hochberg
 * and both are offered in the UI:
 *
 *     significance_data[currency][goal].frequentist[correction][variantId]
 *
 * Production holds the old shape until the stats are regenerated, and both readers
 * resolved it to undefined without erroring: the dashboard rendered an empty
 * Significance section, and the recommendation fell back to significant: false,
 * pValue: 1 and reported "nothing is significant" with confidence. A stats page that
 * quietly lies is worse than one that fails, so this accepts either shape and the
 * deploy order stops being load-bearing.
 *
 * Generic over the variant stats type because the dashboard and the API each declare
 * their own, and this only needs to locate the map, not interpret it.
 */

/** Enough of the shape to tell a variant entry from the `frequentist` branch. */
const isVariantStats = (value: unknown): boolean =>
  typeof value === 'object' && value !== null && 'pValue' in value && 'significant' in value;

export function readFrequentistVariants<T>(
  goalData: Record<string, unknown> | undefined | null,
  correctionMethod: string,
): Record<string, T> | null {
  if (!goalData) return null;

  const nested = goalData.frequentist;
  if (nested && typeof nested === 'object') {
    // Current shape. A missing correction here is a genuine miss, not a reason to
    // fall through to the legacy branch and report uncorrected numbers as corrected.
    const corrected = (nested as Record<string, unknown>)[correctionMethod];
    return corrected && typeof corrected === 'object' ? (corrected as Record<string, T>) : null;
  }

  // Legacy shape: variant ids sit directly under the goal and are uncorrected.
  const variants = Object.entries(goalData).filter(([, value]) => isVariantStats(value));
  return variants.length > 0 ? (Object.fromEntries(variants) as Record<string, T>) : null;
}
